import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { SetURLSearchParams } from 'react-router-dom'

import {
  CMS_MICROSITE_ID,
  CMS_MICROSITE_MESSAGE,
  COMMON_MIXPANEL_PROPERTIES,
  ESSENTIALS_CONTENT_CATEGORY,
  EssentialsContentData,
  getEssentialsSubpath,
  getMicrositePathwayKey,
  INTEGRATED_BENEFITS_EVENTS,
  PAGE_ROUTES,
  PathwayOptions,
  ProviderProgramTaxonomy,
  RESOURCE_COORDINATOR_SCHEDULE_CALL_LINK,
  useGetCMSMicrositeMessageHandlers as useGetCMSMicrositeMessageHandlersShared,
  WELLNESS_TOOLKIT_BASE_ROUTE,
} from '@lyrahealth-inc/shared-app-logic'
import { UTM_CAMPAIGN } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/utm'

import { getScheduleResourceCoordinatorCallLink } from '../../../data/customer/customerSelectors'
import { trackEventWithObj } from '../../../data/mixpanel'
import { useAppDispatch } from '../../../data/storeConfiguration/store'
import { getIsUserLoggedIn } from '../../../data/user/userSelectors'
import { setSelectedPathwayOption, setSelectedPathwaySubpath } from '../../../features/register/data/registerActions'
import { useOpenUrl } from '../../../hooks/useOpenUrl'
import { useGetSearchForCareEventProps } from '../useGetSearchForCareEventProps'
import {
  useNavigateToEssentialsContent,
  useNavigateToEssentialsContentWebProps,
} from '../useNavigateToEssentialsContent'
import { useNavigateToWellnessCheckIn } from '../useNavigateToWellnessCheckIn'

type navigateToEssentialsHomepageWidgetParams = {
  slug?: string
  category?: ESSENTIALS_CONTENT_CATEGORY
  path?: string
}

/**
 * Get post message event handlers based on the cms microsite's metadata
 */
export const useGetCMSMicrositeMessageHandlers = ({
  eventsList,
  page,
  searchParams,
  setSearchParams,
  setIsShowingRegistrationModal,
  micrositeId,
  openCareNavigatorModal,
}: {
  eventsList: { key: CMS_MICROSITE_MESSAGE; params?: Dict }[]
  page: string
  searchParams: URLSearchParams
  setSearchParams: SetURLSearchParams
  setIsShowingRegistrationModal: React.Dispatch<React.SetStateAction<boolean>>
  micrositeId: CMS_MICROSITE_ID
  openCareNavigatorModal: () => void
}): { [key in CMS_MICROSITE_MESSAGE]?: () => void } => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isLoggedIn = useSelector(getIsUserLoggedIn)
  const openUrl = useOpenUrl()
  const navigateToEssentialsContent = useNavigateToEssentialsContent()
  const { navigateToWellnessCheckInForm } = useNavigateToWellnessCheckIn()
  const getAdditionalMixpanelProps = useGetSearchForCareEventProps()
  const scheduleResourceCoordinatorCallLink = useSelector(getScheduleResourceCoordinatorCallLink)

  const registrationEntryPointState = useMemo(() => {
    return {
      state: { registrationEntryPoint: page },
    }
  }, [page])

  const navigateToCareOptions = useCallback(() => {
    navigate(PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE)
  }, [navigate])

  const navigateToCriticalIncidentSupportToolkit = useCallback(() => {
    navigate(WELLNESS_TOOLKIT_BASE_ROUTE + '/trauma')
  }, [navigate])

  const navigateToWellnessCheckIn = useCallback(() => {
    navigateToWellnessCheckInForm({ entryPoint: page })
  }, [navigateToWellnessCheckInForm, page])

  const navigateToEssentials = useCallback(
    ({ navigateToEssentialsHookProps }: { navigateToEssentialsHookProps: useNavigateToEssentialsContentWebProps }) => {
      navigateToEssentialsContent(navigateToEssentialsHookProps)
    },
    [navigateToEssentialsContent],
  )

  const navigateToEssentialsHomepageWidget = useCallback(
    ({ slug, category, path }: navigateToEssentialsHomepageWidgetParams) => {
      const subPathStr = getEssentialsSubpath({ slug, category, subPath: path })
      navigate(PAGE_ROUTES.PATHWAYS.ESSENTIALS_HOMEPAGE_WIDGET, registrationEntryPointState)
      dispatch(setSelectedPathwayOption(PathwayOptions.ESSENTIALS))
      dispatch(setSelectedPathwaySubpath(subPathStr))
    },
    [dispatch, navigate, registrationEntryPointState],
  )

  const navigateToWebinar = useCallback(
    ({ webinarLink }: { webinarLink: string }) => {
      navigate(webinarLink)
    },
    [navigate],
  )

  const showRegistrationModal = useCallback(
    (messageKey: CMS_MICROSITE_MESSAGE) => {
      // Since modal opens on the toolkit, 'page' defaults to the toolkit but 'entryPoint' needs to be set
      const newParams = new URLSearchParams(searchParams)
      newParams.set(COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT, page)
      setSearchParams(newParams)

      setIsShowingRegistrationModal(true)
      dispatch(setSelectedPathwayOption(getMicrositePathwayKey(micrositeId, messageKey)))
    },
    [dispatch, micrositeId, page, searchParams, setIsShowingRegistrationModal, setSearchParams],
  )

  const navigateToRegistration = useCallback(() => {
    navigate('/register/connect', registrationEntryPointState)
  }, [navigate, registrationEntryPointState])

  const clickSelfCareContent = useCallback(
    ({ contentData, contentSlug }: { contentData?: EssentialsContentData; contentSlug?: string }) => {
      const slug = contentData?.slug || contentSlug
      if (slug) {
        navigateToEssentialsContent({ slug, newWindow: true, loggedIn: isLoggedIn })
      }
    },
    [navigateToEssentialsContent, isLoggedIn],
  )

  const navigateToProviderBooking = useCallback(
    ({ providerId, ctpo, newTab }: { providerId: string; ctpo?: ProviderProgramTaxonomy; newTab?: boolean }) => {
      if (newTab) {
        const url = new URL(`${window.location.origin}/secure/index/provider/${providerId}`)
        url.searchParams.set('utm_campaign', UTM_CAMPAIGN.PARENTAL_LEAVE_COACHING)
        if (ctpo) {
          Object.entries(ctpo).forEach(([key, value]) => {
            url.searchParams.set(key, String(value))
          })
        }
        window.open(url.toString(), '_blank', 'noopener')
      } else {
        navigate(PAGE_ROUTES.PROVIDER_PROFILE(providerId), { state: { ctpo } })
      }
    },
    [navigate],
  )

  const handleScheduleResourceCoordinatorCall = useCallback(
    ({ message }: { message: MessageEvent }) => {
      const locationOnPage = message.data?.mixPanelData?.entrypoint ?? ''
      dispatch(
        trackEventWithObj({
          event: INTEGRATED_BENEFITS_EVENTS.CLICK_SCHEDULE_RESOURCE_COORDINATOR_CALL,
          ...getAdditionalMixpanelProps({}, page),
          [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: locationOnPage,
        }),
      )
      openUrl(scheduleResourceCoordinatorCallLink ?? RESOURCE_COORDINATOR_SCHEDULE_CALL_LINK)
    },
    [dispatch, getAdditionalMixpanelProps, openUrl, page, scheduleResourceCoordinatorCallLink],
  )

  return useGetCMSMicrositeMessageHandlersShared({
    eventsList,
    isLoggedIn,
    navigateToCareOptions,
    navigateToWellnessCheckIn,
    navigateToCriticalIncidentSupportToolkit,
    navigateToEssentials,
    navigateToEssentialsHomepageWidget,
    navigateToWebinar,
    navigateToRegistration,
    showRegistrationModal,
    clickSelfCareContent,
    openExternalLink: ({ link }) => openUrl(link),
    navigateToProviderBooking,
    openCareNavigatorModal,
    scheduleResourceCoordinatorCall: handleScheduleResourceCoordinatorCall,
  })
}
